/**
 * Furthest-step tracker.
 *
 * Tracks the deepest onboarding milestone the user has reached so that we
 * can bucket returning users into the Quiz Abandoner Menu audiences directly
 * (`beforeMealPrep` / `afterMealPrep` / `atCheckout`)
 */

import Cookies from 'js-cookie';
import { MILESTONES, QUIZ_MILESTONES, STATE } from './constants';

const CART_COOKIE_NAME = 'shopifyCartId';
const STORAGE_KEY_PREFIX = 'furthestStep:';

const EXCLUDED_MILESTONES = new Set([STATE.initial, STATE.submitCart]);

const STATE_BY_PATH = Object.fromEntries(
  Object.entries(STATE).map(([key, path]) => [path, key]),
);

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage;

const milestoneIndex = milestone => MILESTONES.indexOf(milestone);

const getStorageKey = cartId => `${STORAGE_KEY_PREFIX}${cartId}`;

const getCartIdFromCookie = () => {
  try {
    return Cookies.get(CART_COOKIE_NAME) ?? null;
  } catch {
    return null;
  }
};

/**
 * Clean up localStorage entries with `furthestStep:*` prefix
 *
 * exceptCartId: optionl prop used when writing the current
 * cart's marker so we don't delete then rewrite the same key
 */
const clearStaleFurthestStepEntries = (exceptCartId = null) => {
  if (!isBrowser()) return;
  try {
    const exceptKey = exceptCartId ? getStorageKey(exceptCartId) : null;
    const keysToRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX) && key !== exceptKey) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => window.localStorage.removeItem(key));
  } catch {}
};

export const readFurthestStep = () => {
  if (!isBrowser()) return null;

  const cartId = getCartIdFromCookie();
  if (!cartId) return null;

  let raw;
  try {
    raw = window.localStorage.getItem(getStorageKey(cartId));
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    try {
      window.localStorage.removeItem(getStorageKey(cartId));
    } catch {}
    return null;
  }

  if (!parsed || typeof parsed.step !== 'string') return null;
  if (!MILESTONES.includes(parsed.step)) return null;

  return parsed.step;
};

/**
 * Writes `step` if it's at or beyond the currently stored milestone.
 */
export const writeFurthestStep = step => {
  if (!isBrowser()) return;
  if (!MILESTONES.includes(step)) return;

  const cartId = getCartIdFromCookie();
  if (!cartId) return;

  const current = readFurthestStep();
  if (current && milestoneIndex(current) >= milestoneIndex(step)) return;

  const storageKey = getStorageKey(cartId);

  try {
    if (!window.localStorage.getItem(storageKey)) {
      clearStaleFurthestStepEntries(cartId);
    }
    window.localStorage.setItem(storageKey, JSON.stringify({ step }));
  } catch {}
};

export const clearFurthestStep = () => clearStaleFurthestStepEntries();

/**
 * Map a state machine state name to a milestone id.
 */
export const milestoneFromStateName = stateName => {
  if (!stateName || typeof stateName !== 'string') return null;
  if (EXCLUDED_MILESTONES.has(stateName)) return null;

  const key = STATE_BY_PATH[stateName];

  return key && MILESTONES.includes(key) ? key : null;
};

/**
 * Map a furthest-step milestone to the bucket used by the QuizAbandonerMenu.
 */
export const bucketFromFurthestStep = step => {
  if (!step) return null;
  if (step === 'intro' || QUIZ_MILESTONES.includes(step))
    return 'beforeMealPrep';
  if (step === 'checkout') return 'atCheckout';
  return 'afterMealPrep';
};

export const FURTHEST_STEP_STORAGE_KEY_PREFIX = STORAGE_KEY_PREFIX;
export const FURTHEST_STEP_COOKIE_KEY = CART_COOKIE_NAME;
